/**
 * GENERATED from source/affordances-platform.md and source/affordances-cert.md, verbatim. Do not edit the words here: edit the doc
 * (Coleman's) and regenerate, or the verbatim test fails. Structure (block
 * kinds, chapters, exercises, and each block's depth) is decided in
 * scripts/build-concepts.py; depth.js renders the Platform and
 * Certification versions from this one master lesson. See CLAUDE.md,
 * "Concept modules are verbatim", and .claude/skills/concept-module/SKILL.md.
 */

export const AFFORDANCES = {
  "key": "affordances",
  "title": "Affordances",
  "category": "Concepts",
  "platform": {
    "key": "affordances",
    "sub": "What Does the Player Actually See?",
    "duration": "8 min read",
    "chapter": {
      "id": "affordances",
      "title": "What Does the Player Actually See?"
    }
  },
  "cert": {
    "key": "affordances-cert",
    "title": "Affordances: Deeper Dive",
    "duration": "20–25 min"
  },
  "chapters": [
    {
      "id": "simple",
      "title": "The simple idea",
      "blocks": [
        {
          "kind": "heading",
          "level": 3,
          "text": "The simple idea"
        },
        {
          "kind": "text",
          "md": "An **affordance** is an opportunity for action.\n\nThat's the simple definition.\n\nBut the interesting part is that two people can exist in the same environment and not necessarily perceive the same opportunities.\n\nWhat becomes available to us depends on the relationship between:\n\n- who we are,\n- what we can do,\n- what information matters to us,\n- and the environment we're interacting with.\n\nThat becomes really important when we're coaching decision-making."
        },
        {
          "kind": "text",
          "md": "You do **not** need to become an ecological psychologist to use this well.\n\nBut understanding some of the science underneath it can make the practical idea much clearer.",
          "depth": "cert"
        },
        {
          "kind": "heading",
          "level": 3,
          "text": "Where Did Affordances Come From?",
          "depth": "cert"
        },
        {
          "kind": "text",
          "md": "The concept is most closely associated with psychologist **James J. Gibson** and his ecological approach to perception.\n\nGibson was interested in understanding perception in relation to action.\n\nRather than treating the environment simply as a collection of objects and physical properties, his work emphasized what the environment **offers an individual the opportunity to do**.\n\nThis became one of the central ideas in his 1979 book The Ecological Approach to Visual Perception.\n\nThat gives us the basic shift:\n\nInstead of only asking:\n\n**“What is in the environment?”**\n\nwe can also ask:\n\n**“What can this individual do within it?”**",
          "depth": "cert"
        },
        {
          "kind": "scienceNerd",
          "blocks": [
            {
              "kind": "prose",
              "md": "One reason Gibson's approach was so influential was that it pushed against explanations of perception that treated perception and action as largely separate steps.\n\nThe ecological approach emphasizes the ongoing relationship between the organism and the environment.\n\nFor our purposes, you don't need to resolve every theoretical debate around direct perception.\n\nThe important point is that an affordance isn't understood only by measuring the environment.\n\nYou also have to consider the organism acting within it."
            }
          ],
          "depth": "cert"
        },
        {
          "kind": "heading",
          "level": 3,
          "text": "You Already Experience This Every Day"
        },
        {
          "kind": "text",
          "md": "Imagine you love sneakers.\n\nYou know the brands, models and colorways. You buy a lot of shoes and naturally pay attention to what people are wearing.\n\nYou're walking down the street with a friend who couldn't care less about sneakers.\n\nSomeone walks by wearing a pair you love.\n\nYou immediately see an opportunity:\n\n**“Those are nice. Where'd you get them?”**\n\nYour friend may barely even notice the shoes.\n\nSame street.\n\nSame person.\n\nSame sneakers.\n\nDifferent opportunities."
        },
        {
          "kind": "text",
          "md": "Now imagine something sitting on a high shelf.\n\nFor a taller person, the shelf may simply afford:\n\n**Grab it.**\n\nFor someone much shorter, it may afford:\n\n**Find a stool.**\n\nOr:\n\n**Ask somebody for help.**\n\nThe shelf didn't change.\n\n**The person did.**\n\nAnd that's one of the most important things to understand about affordances.\n\nThe environment doesn't exist completely separately from the person trying to act within it."
        }
      ]
    },
    {
      "id": "action",
      "title": "What You Can Do Changes What Becomes Available",
      "blocks": [
        {
          "kind": "heading",
          "level": 3,
          "text": "What You Can Do Changes What Becomes Available"
        },
        {
          "kind": "text",
          "md": "This is where it starts becoming really useful for coaches.\n\nTwo athletes can face a very similar situation but have very different action possibilities because they have different capabilities.\n\nSometimes we see a player repeatedly fail to take an action and assume:\n\n**“They don't see it.”**\n\nMaybe.\n\nBut before we decide they have a perception problem, there's another question we should ask:\n\n**Can they actually do it?**\n\nBecause what an athlete is capable of doing can influence what becomes meaningful as an opportunity in the first place.\n\nThis performer-environment relationship is one of the foundations of affordance research. Classic experiments have shown that perceived opportunities for action can scale to the individual's own body and capabilities rather than a single universal measurement of the environment."
        },
        {
          "kind": "heading",
          "level": 3,
          "text": "Affordances Are Relational",
          "depth": "cert"
        },
        {
          "kind": "text",
          "md": "This is the key piece.\n\nImagine something on a high shelf.\n\nThe shelf has an objective height.\n\nBut whether it affords simply reaching out and grabbing the object depends on the person.\n\nIn other words:\n\n**Environmental information + individual capabilities = action possibilities.**\n\nThat's why we call affordances **relational**.\n\nAnd there is classic experimental evidence demonstrating this idea.",
          "depth": "cert"
        },
        {
          "kind": "heading",
          "level": 3,
          "text": "Warren's Stair-Climbing Study",
          "depth": "cert"
        },
        {
          "kind": "text",
          "md": "In 1984, researcher William Warren studied how people judged whether stairs were climbable.\n\nHe used participants of different heights and varied the height of the stair risers.\n\nIf climbability were based only on an objective stair height, we might expect people to share roughly the same cutoff.\n\nInstead, Warren found that people's judgments scaled relative to their own bodies.\n\nWhen stair height was expressed relative to participants' leg length, judgments from shorter and taller participants lined up much more closely.\n\nThe specific number isn't very important for us.\n\nThe principle is:\n\n**The step did not afford the exact same action independently of the person trying to climb it.**",
          "depth": "cert"
        },
        {
          "kind": "scienceNerd",
          "blocks": [
            {
              "kind": "prose",
              "md": "Warren described this relationship using a dimensionless ratio between stair height and leg length.\n\nThe critical value was around **0.88 relative to leg length** in the young adults studied.\n\nThat means the meaningful variable wasn't simply:\n\n**“How high is the stair?”**\n\nIt was closer to:\n\n**“How high is this stair relative to this person's ability to climb it?”**\n\nThis became an influential example of what is often called **body-scaled affordance perception.**"
            }
          ],
          "depth": "cert"
        },
        {
          "kind": "heading",
          "level": 3,
          "text": "From Body-Scaled to Action-Scaled",
          "depth": "cert"
        },
        {
          "kind": "text",
          "md": "For coaches, body dimensions are only part of the story.\n\nWe don't just care how tall or long-limbed somebody is.\n\nWe care about:\n\n**What can this athlete actually do?**\n\nThis gives us the practical idea of **action-scaled affordances**.\n\nTwo athletes may experience the same situation differently because their functional ability to act is different.\n\nAnd this gives us a major coaching implication:",
          "depth": "cert"
        },
        {
          "kind": "bigIdea",
          "md": "Sometimes what appears to be a decision-making problem may actually begin as an action-capacity problem.",
          "depth": "cert"
        },
        {
          "kind": "heading",
          "level": 3,
          "text": "Your State Matters Too"
        },
        {
          "kind": "text",
          "md": "Imagine you're driving toward a yellow light.\n\nSame person.\n\nSame car.\n\nSimilar distance from the intersection.\n\nOne day you're energized and in a rush.\n\nThe light may invite:\n\n**Go.**\n\nAnother day you've had a long day, you're relaxed and have nowhere to be.\n\nThat same yellow light may invite:\n\n**Stop.**\n\nThe environment is very similar.\n\n**You aren't.**\n\nWe think the same general idea matters in basketball.\n\nImagine a player catches the ball early in the game.\n\nThey're not fully warm or primed yet.\n\nA situation may invite them to:\n\n**Swing it one more.**\n\nLater in the game, they're warmer, more involved and more confident.\n\nA similar situation may now invite them to:\n\n**Shoot.**\n\nOr:\n\n**Drive.**\n\nSame athlete.\n\nSimilar situation.\n\nDifferent moment.\n\nAt BAM, we pay attention to things like physical readiness, confidence, rhythm, intention and current state because all of these can influence which opportunities actually become meaningful enough for an athlete to act on."
        }
      ]
    },
    {
      "id": "read",
      "title": "There Isn't Always One “Correct Read”",
      "blocks": [
        {
          "kind": "heading",
          "level": 3,
          "text": "There Isn't Always One “Correct Read”"
        },
        {
          "kind": "text",
          "md": "This is where affordances can change the way we coach.\n\nCoaches love to freeze film and say:\n\n**“That's the read.”**\n\nSometimes it is pretty clear.\n\nBut the player wasn't experiencing a freeze frame.\n\nThere can be much more information shaping their behavior than we realize."
        },
        {
          "kind": "heading",
          "level": 5,
          "text": "Waving Off the Ball Screen",
          "depth": "platform"
        },
        {
          "kind": "text",
          "md": "A player waves off a ball screen.\n\nThe action you're expecting may be:\n\n**Use the screen.**\n\nBut the player knows that their defender has had trouble guarding them to the left on the last few possessions.\n\nThe defender is tired.\n\nThe player believes they can make a move right now and create an advantage.\n\nFrom the sideline, we may think:\n\n**“Why didn't you use the screen?”**\n\nThe athlete may have perceived a different opportunity as more attractive.",
          "depth": "platform"
        },
        {
          "kind": "heading",
          "level": 5,
          "text": "The Transition Pass",
          "depth": "platform"
        },
        {
          "kind": "text",
          "md": "A player is pushing the ball in transition.\n\nA teammate is ahead of them.\n\nThe advanced pass looks open.\n\nBut that teammate has just been talking trash to an opponent in a close game and is visibly angry.\n\nThe ball handler may believe that passing them the ball right now is likely to lead to an ill-advised drive into two defenders.\n\nThe pass may exist.\n\nThat doesn't automatically mean it feels like the best option to the athlete.",
          "depth": "platform"
        },
        {
          "kind": "heading",
          "level": 5,
          "text": "The Wide-Open Shot",
          "depth": "platform"
        },
        {
          "kind": "text",
          "md": "A player catches the ball with an open shot.\n\nThe coach sees:\n\n**Shoot.**\n\nBut the player doesn't feel in rhythm.\n\nThey take an extra dribble.\n\nThat dribble may help them find rhythm or allow them to attack the long closeout depending on what the defender does.\n\nThe shot existed.\n\nBut it wasn't necessarily the action that felt most inviting in that moment.",
          "depth": "platform"
        },
        {
          "kind": "heading",
          "level": 3,
          "text": "One Environment Can Offer Multiple Opportunities",
          "depth": "cert"
        },
        {
          "kind": "text",
          "md": "Another important idea is that the game doesn't necessarily present one affordance at a time.\n\nMultiple opportunities can coexist.\n\nAnd as information changes, one may become more or less attractive.\n\nThis is why binary coaching language can sometimes oversimplify decisions.\n\n**Shoot or pass.**\n\n**Use the screen or don't.**\n\n**Throw it ahead or don't.**\n\nThe athlete may be navigating several changing possibilities at once.",
          "depth": "cert"
        },
        {
          "kind": "heading",
          "level": 3,
          "text": "There May Be More Information Than We Think"
        },
        {
          "kind": "text",
          "md": "This doesn't mean there are no bad decisions.\n\nAnd it doesn't mean every decision is equally good.\n\nIt means coaches should be careful about assuming that the athlete was choosing between the exact same options that we see afterward.\n\nMaybe a player doesn't counter after getting cut off because they're slightly losing control of the ball.\n\nMaybe they rolled the ankle they were going to plant off on the previous possession.\n\nMaybe they're intentionally driving deeper into a gap because they want to create a kick-out.\n\nMaybe they're coming off a ball screen and don't trust the shooter they're considering passing to.\n\nMaybe they probe longer than usual because they're currently out of rhythm with their own shot.\n\nThere can be much more shaping behavior than:\n\n**“Did they make the right read?”**"
        }
      ]
    },
    {
      "id": "check",
      "title": "The BAM Affordance Check",
      "blocks": [
        {
          "kind": "heading",
          "level": 3,
          "text": "The BAM Affordance Check"
        },
        {
          "kind": "text",
          "md": "When a player repeatedly doesn't take an action we expected, we like to work through four questions."
        },
        {
          "kind": "heading",
          "level": 5,
          "text": "1. CAN THEY DO IT?"
        },
        {
          "kind": "text",
          "md": "Do they actually have the physical and technical capacity to execute the action?\n\nIf not, we may not primarily have a decision-making problem.\n\nWe may have an action-capacity problem."
        },
        {
          "kind": "heading",
          "level": 5,
          "text": "2. DID THEY SEE IT?"
        },
        {
          "kind": "text",
          "md": "If they can physically execute the action:\n\n**Did the opportunity actually emerge for them?**\n\nSomething can look obvious after we pause the film without ever having felt obvious to the athlete in real time."
        },
        {
          "kind": "heading",
          "level": 5,
          "text": "3. DID THEY TRUST IT?"
        },
        {
          "kind": "text",
          "md": "If they could do it and they saw it:\n\n**Were they confident enough to act?**\n\nAt BAM, we treat confidence as one of the major gates on behavior.\n\nA player may recognize an opportunity without trusting themselves enough to take it.\n\nSometimes that threshold of confidence is extremely low.\n\nSometimes it's much higher."
        },
        {
          "kind": "heading",
          "level": 5,
          "text": "4. WHAT ELSE MATTERED?"
        },
        {
          "kind": "text",
          "md": "If they could do it, saw it and trusted it:\n\n**Why did another solution still win out?**\n\nWhat else was the player responding to?\n\nMaybe another opportunity became more attractive.\n\nMaybe something happened on the previous possession.\n\nMaybe they were trying to create something later in the possession.\n\nMaybe information about a teammate changed what they valued.\n\nMaybe game context changed their intention.\n\nThis is where coaching becomes investigation rather than simply correction."
        },
        {
          "kind": "heading",
          "level": 3,
          "text": "Sometimes the Athlete Doesn't Need Another “Read”",
          "depth": "cert"
        },
        {
          "kind": "text",
          "md": "Let's return to our first question.",
          "depth": "cert"
        },
        {
          "kind": "heading",
          "level": 5,
          "text": "Can they do it?",
          "depth": "cert"
        },
        {
          "kind": "text",
          "md": "Before we tell the player:\n\n**“You have to see that.”**\n\nwe should ask whether they can reliably execute the action we're expecting.\n\nIf they cannot, continuously telling them to recognize the opportunity may not address the actual limitation.\n\nAs their capabilities change, what becomes actionable may change as well.\n\nThe practical implication is important:\n\n**Developing action can also change perception.**",
          "depth": "cert"
        },
        {
          "kind": "scienceNerd",
          "blocks": [
            {
              "kind": "prose",
              "md": "This is part of why ecological approaches resist separating perception and action too cleanly.\n\nPerception is being calibrated relative to what the individual can actually do.\n\nThe athlete is not simply perceiving the world first and then independently choosing an action afterward.\n\nThe two processes are tightly related.\n\nThat doesn't mean every change in ability automatically produces perfect perception.\n\nIt means we should be cautious about treating perceptual skill and action skill as totally separate systems."
            }
          ],
          "depth": "cert"
        },
        {
          "kind": "heading",
          "level": 3,
          "text": "Seeing It Is Not the Same as Taking It",
          "depth": "cert"
        },
        {
          "kind": "text",
          "md": "Now imagine the player **can** physically execute the action.\n\nOur next question is:\n\n**Did they see it?**\n\nAnd after that:\n\n**Did they trust it enough to do it?**\n\nThis is where we make an important distinction between ecological theory and BAM's applied framework.\n\nConfidence is extremely important to how we coach decisions.\n\nBut we don't need to pretend that:\n\n**Gibson's definition of affordance = opportunity + confidence.**\n\nThat's not what we're saying.\n\nWe're saying that when coaching real athletes, we care about what happens between an action being possible, an opportunity being perceived, and the athlete actually expressing it.\n\nAt BAM, confidence is one of the biggest gates we investigate.",
          "depth": "cert"
        },
        {
          "kind": "heading",
          "level": 3,
          "text": "Confidence as a Gate",
          "depth": "cert"
        },
        {
          "kind": "text",
          "md": "Imagine an athlete sees an opportunity.\n\nThey may even physically possess the solution.\n\nBut they don't trust it enough to act.\n\nThis is common.\n\nEvery action requires some level of commitment.\n\nAnd we think the confidence required to cross that threshold can change depending on:\n\n- the action,\n- the player,\n- the game,\n- the moment,\n- and the player's current state.\n\nA player doesn't need maximum confidence to act.\n\nThey just need **enough**.\n\nAnd what counts as enough can change.",
          "depth": "cert"
        },
        {
          "kind": "scienceNerd",
          "blocks": [
            {
              "kind": "prose",
              "md": "This is an important place to distinguish between:\n\n**what the affordance literature itself establishes**\n\nand\n\n**how BAM uses the concept as a coaching framework.**\n\nOur confidence model is an applied interpretation.\n\nWe use it because it helps us diagnose why an athlete may perceive an available action without expressing it.\n\nThat doesn't mean confidence should automatically be inserted into the formal definition of an affordance.\n\nKeeping those two layers separate makes the model more scientifically honest and more practically useful."
            }
          ],
          "depth": "cert"
        },
        {
          "kind": "heading",
          "level": 3,
          "text": "Did They Not See It, or Did They Not Act on It?"
        },
        {
          "kind": "text",
          "md": "This distinction can be really difficult.\n\nImagine a player catches the ball open.\n\nThey get into realistic shooting footwork and begin preparing to shoot.\n\nThen they pass.\n\nThat gives us some evidence that the shooting opportunity mattered to them before another option became more attractive.\n\nNow compare that with a player who catches already organized to pass and immediately moves the ball.\n\nThat gives us a different clue.\n\nMaybe shooting never meaningfully emerged as an option in the first place.\n\nBut we need to be careful.\n\nThese are **clues, not certainty.**"
        },
        {
          "kind": "heading",
          "level": 3,
          "text": "How Do We Investigate What They Saw?"
        },
        {
          "kind": "heading",
          "level": 5,
          "text": "Look at their movement"
        },
        {
          "kind": "text",
          "md": "How did the athlete organize their body?\n\nDid they begin preparing for one action before changing to another?"
        },
        {
          "kind": "heading",
          "level": 5,
          "text": "Look at their visual orientation"
        },
        {
          "kind": "text",
          "md": "Where did their eyes and body appear to orient?\n\nThis can provide useful clues, but looking somewhere doesn't prove exactly what an athlete perceived."
        },
        {
          "kind": "heading",
          "level": 5,
          "text": "Ask them"
        },
        {
          "kind": "text",
          "md": "Instead of immediately saying:\n\n**“Why didn't you shoot?”**\n\ntry:\n\n**“What did you see here?”**\n\nThose are very different questions.\n\nThe first contains our interpretation.\n\nThe second investigates theirs.\n\nEven asking isn't perfect.\n\nPlayers don't always accurately reconstruct what they perceived after the fact.\n\nSo the goal isn't to read their mind.\n\nIt's to collect better evidence."
        },
        {
          "kind": "heading",
          "level": 3,
          "text": "The Main Takeaway"
        },
        {
          "kind": "text",
          "md": "The next time you catch yourself saying:\n\n**“They missed the read.”**\n\nRun through this first:\n\n**Can they do it?**\n\n**Did they see it?**\n\n**Did they trust it?**\n\n**What else mattered?**\n\nAffordances give us a way to look beyond whether an option simply existed on the court.\n\nThey help us ask a more useful question:\n\n**What did this situation actually offer this athlete in this moment?**\n\nThe better we understand that, the better we can understand the behavior we're actually trying to coach."
        },
        {
          "kind": "reflection",
          "md": "The next time you're watching film and something looks like an obvious decision, resist pausing immediately to tell the athlete the answer.\n\nAsk yourself:\n\n**What information would I need before I actually know why they made this choice?**"
        }
      ]
    },
    {
      "id": "cases",
      "title": "CASE STUDY 1: Waving Off the Ball Screen",
      "blocks": [
        {
          "kind": "heading",
          "level": 3,
          "text": "CASE STUDY 1: Waving Off the Ball Screen",
          "depth": "cert"
        },
        {
          "kind": "text",
          "md": "A player waves off a ball screen.\n\nYou know:\n\n- their defender has struggled guarding them to the left over the previous possessions,\n- the defender is tired,\n- and the player believes they can immediately create an advantage.\n\nYour normal offensive expectation is for them to use the ball screen.",
          "depth": "cert"
        },
        {
          "kind": "heading",
          "level": 5,
          "text": "Your Question",
          "depth": "cert"
        },
        {
          "kind": "writtenResponse",
          "id": "case1",
          "prompt": "Does waving the screen off automatically make this the wrong decision?",
          "hint": "Before continuing, write down:\n\n**What would you need to know before deciding?**",
          "discussion": "We don't have enough information yet.\nMaybe rejecting the screen was an excellent decision.\nMaybe it wasn't.\nAffordances don't mean:\n**Every player decision is defensible.**\nThey mean we investigate the relationship between the player and the situation before assuming the system or freeze frame contains the only possible answer.\nAsk:\n**Could they actually create the advantage?**\n**What did they perceive in the defender?**\n**How confident were they?**\n**What other information mattered?**\nThen evaluate the decision.",
          "depth": "cert"
        },
        {
          "kind": "heading",
          "level": 3,
          "text": "CASE STUDY 2: The Transition Pass",
          "depth": "cert"
        },
        {
          "kind": "text",
          "md": "A player is pushing in transition.\n\nA teammate is ahead.\n\nThe pass appears available.\n\nBut the teammate has just been talking trash to an opponent in a close game and is visibly angry.\n\nThe ball handler chooses not to advance the ball because they believe the teammate may make an ill-advised drive into two defenders.",
          "depth": "cert"
        },
        {
          "kind": "multipleChoice",
          "id": "case2",
          "prompt": "Which statement do you agree with most?",
          "options": [
            {
              "id": "a",
              "label": "**A.** The teammate was open, so the ball handler missed the read."
            },
            {
              "id": "b",
              "label": "**B.** The pass existed, but additional social and contextual information may have made another action preferable."
            },
            {
              "id": "c",
              "label": "**C.** Because affordances are individual, there is no such thing as a bad decision."
            }
          ],
          "discussion": "**B** is the best interpretation.\nAnd C is an important misconception.\nAffordances do not mean:\n**Everything is subjective, so every decision is fine.**\nWe can still evaluate the effectiveness of a decision.\nWe just want to understand what produced it before we coach it.",
          "depth": "cert"
        },
        {
          "kind": "heading",
          "level": 3,
          "text": "CASE STUDY 3: The Open Shot",
          "depth": "cert"
        },
        {
          "kind": "text",
          "md": "A player catches with an open shot.\n\nThey're currently out of rhythm.\n\nThey take one extra dribble.\n\nThat dribble may help them find rhythm or allow them to attack the long closeout depending on what the defender does.",
          "depth": "cert"
        },
        {
          "kind": "diagnostic",
          "id": "case3",
          "intro": "Now use the BAM Affordance Check.",
          "prompts": [
            {
              "id": "can",
              "q": "1. Can they do it?",
              "hint": "What evidence do you have about their ability to make the original shot?"
            },
            {
              "id": "saw",
              "q": "2. Did they see it?",
              "hint": "What could help you determine whether shooting actually emerged as an option for them?"
            },
            {
              "id": "trust",
              "q": "3. Did they trust it?",
              "hint": "What might indicate that confidence influenced the decision?"
            },
            {
              "id": "else",
              "q": "4. What else mattered?",
              "hint": "What information may have made the extra dribble more attractive?"
            }
          ],
          "outro": "Write your answers before revealing the discussion.",
          "discussion": "**Reading the Player's Behavior**\nOne clue may be how the athlete organizes their body.\nIf the player catches, organizes into realistic shooting footwork and begins preparing to shoot before passing, we have some evidence that shooting was likely meaningful to them.\nIf they catch already organized to pass and immediately move the ball, we have different evidence.\nNeither one tells us exactly what was happening internally.\nThat's why we treat:\n**movement**\n**gaze**\n**player reports**\n**and repeated patterns**\nas evidence rather than certainty.",
          "depth": "cert"
        }
      ]
    },
    {
      "id": "apply",
      "title": "APPLICATION: USE IT ON YOUR OWN PLAYER",
      "blocks": [
        {
          "kind": "heading",
          "level": 3,
          "text": "APPLICATION: USE IT ON YOUR OWN PLAYER",
          "depth": "cert"
        },
        {
          "kind": "applyToAthlete",
          "id": "apply",
          "numbered": false,
          "intro": "Now choose a player you actually coach.\n\nThink about a situation where you've repeatedly thought:\n\n**“Why aren't they making this read?”**",
          "steps": [
            {
              "id": "what",
              "q": "Step 1",
              "hint": "Describe exactly what keeps happening.",
              "rows": 4
            },
            {
              "id": "expect",
              "q": "Step 2",
              "hint": "What action do you believe should be available?",
              "rows": 4
            },
            {
              "id": "can",
              "q": "Step 3: Can they do it?",
              "hint": "What physical and technical abilities are required?\n\nDo you have evidence they can execute the action under realistic conditions?",
              "rows": 4
            },
            {
              "id": "saw",
              "q": "Step 4: Did they see it?",
              "hint": "What information would they need to detect?\n\nWhat evidence suggests they are or aren't detecting it?",
              "rows": 4
            },
            {
              "id": "trust",
              "q": "Step 5: Did they trust it?",
              "hint": "How confident do you believe they are in the action?\n\nWhat evidence supports that?",
              "rows": 4
            },
            {
              "id": "else",
              "q": "Step 6: What else mattered?",
              "hint": "What might you be overlooking?\n\nConsider:\n\n- previous possessions,\n- teammates,\n- opponent behavior,\n- rhythm,\n- game state,\n- physical state,\n- psychological state.",
              "rows": 4
            },
            {
              "id": "intervention",
              "q": "Step 7",
              "hint": "Now choose your intervention.\n\nDoes the athlete need:\n\n- greater action capacity?\n- more exposure to the relevant situation?\n- help becoming attuned to certain information?\n- more confidence in the action?\n- a different practice environment?\n- or simply more observation before you decide?\n\nExplain your reasoning.",
              "rows": 4
            }
          ],
          "depth": "cert"
        },
        {
          "kind": "heading",
          "level": 3,
          "text": "FINAL REFLECTION",
          "depth": "cert"
        },
        {
          "kind": "text",
          "md": "Return to the original statement:\n\n**“They're not making the read.”**\n\nAfter working through the affordance framework:",
          "depth": "cert"
        },
        {
          "kind": "writtenResponse",
          "id": "final",
          "prompt": "**Would you still describe this primarily as a decision-making problem?**",
          "hint": "Why?",
          "depth": "cert"
        },
        {
          "kind": "heading",
          "level": 3,
          "text": "Final Takeaway",
          "depth": "cert"
        },
        {
          "kind": "text",
          "md": "Affordances don't eliminate good and bad decisions.\n\nThey help us better understand where decisions come from.\n\nThe game does not necessarily present every athlete with the exact same menu of actions.\n\nBehavior emerges from a relationship between:\n\n**the athlete**\n\n**what they can do**\n\n**what they perceive**\n\n**what they trust**\n\n**what they're trying to accomplish**\n\n**and the environment they're acting within**\n\nSometimes the answer really is:\n\n**“They made a bad read.”**\n\nBut the affordance framework pushes us to earn that conclusion rather than assuming it.\n\nBefore correcting the player, investigate the behavior.",
          "depth": "cert"
        },
        {
          "kind": "relatedConcepts",
          "items": [
            {
              "key": "repetition-without-repetition",
              "title": "Repetition Without Repetition",
              "blurb": "How to Get More Reps Without Just Reciting the Same Solution"
            },
            {
              "key": "types-of-variability",
              "title": "Types of Variability",
              "blurb": "Stable enough to perform. Adaptable enough to solve."
            }
          ]
        }
      ]
    }
  ]
};
